import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import type { View, WeekYear } from "@chef/types/common";
import type { StandaloneBasketVariant } from "@chef/types/basket";

import { PickAndMixQuery } from "../graphql/generated";
import { IDeviationProduct, RootState } from "../";

export interface ICartModal {
  open: boolean;
  week: number | null;
  year: number | null;
}

export interface IResumeOngoingDeviationModal {
  open: boolean;
  week: number;
  year: number;
  deviationProducts: IDeviationProduct[];
  calendarBasketProducts: IDeviationProduct[];
  destinationUrl?: string;
}
export interface IPreselectedRecipesModal {
  open: boolean;
  preferenceIds: string[];
}

export type StandaloneProductModalParams = {
  id: "STANDALONE_PRODUCT";
  productId: string;
  variationId: string;
  week: number;
  year: number;
  view: View;
  basketType?: StandaloneBasketVariant;
};

export type SimpleModal = {
  open: boolean;
};

type MyMenuSelectWeekModalParams = {
  id: "MY_MENU_SELECT_WEEK";
};

export type RecipeModalParams = {
  id: "RECIPE";
  recipeId: number;
  pickAndMixProduct?: PickAndMixQuery["pickAndMix"][0] | null;
  misc?: {
    portionSize?: string;
    updateProduct?: {
      productId: string;
      variationId: string;
      mainRecipeId: number | null;
      recipeId: number;
      recipeName: string;
      price: number;
      sortingPosition: number;
      recommendationScore: number;
    };
  } | null;
  view?: "historic" | "deviation" | "conversion";
};

export type SubscribedStandaloneModalParams = WeekYear & {
  id: "SUBSCRIBED_STANDALONE";
  deviationProduct: IDeviationProduct | null;
  productName: string | null;
  maxQtyAllowed: number | null;
};

export type WeekSchedulerEditorModalParams = WeekYear & {
  id: "WEEK_SCHEDULER_EDITOR";
};

export type OverrideFeatureFlagsModalParams = {
  id: "OVERRIDE_FEATURE_FLAGS";
};

export type ResumeOngoingDeviationModalParams = WeekYear & {
  id: "RESUME_ONGOING_DEVIATION";
  deviationProducts: IDeviationProduct[];
  calendarBasketProducts: IDeviationProduct[];
  destinationUrl: string;
};

export type PersonalisedRecipesModalParams = WeekYear & {
  id: "PERSONALISED_RECIPES";
  conceptPreferenceIds: string[];
  tastePreferenceIds: string[];
  portions: number;
};

export type WeekPortionModalProps = WeekYear & {
  id: "WEEK_PORTION";
} & WeekYear;

export type ConfirmRevertModalProps = {
  id: "CONFIRM_REVERT";
} & WeekYear;

export type ModalData =
  | MyMenuSelectWeekModalParams
  | RecipeModalParams
  | StandaloneProductModalParams
  | SubscribedStandaloneModalParams
  | WeekSchedulerEditorModalParams
  | OverrideFeatureFlagsModalParams
  | ResumeOngoingDeviationModalParams
  | PersonalisedRecipesModalParams
  | WeekPortionModalProps
  | ConfirmRevertModalProps;

export type Modal =
  | {
      open: false;
      data: null;
    }
  | {
      open: true;
      data: ModalData;
    };

const initialState = {
  modal: {
    open: false,
    data: null,
  } as Modal,

  cart: {
    open: false,
    week: null,
    year: null,
  } as ICartModal,

  drawer: {
    open: false,
  } as SimpleModal,

  login: {
    open: false,
  } as SimpleModal,

  recipeAction: {
    open: false,
  } as SimpleModal,

  preselectedRecipes: {
    open: false,
    preferenceIds: [],
  } as IPreselectedRecipesModal,
};

export const modalSlice = createSlice({
  name: "modal",
  initialState,
  reducers: {
    openModal: (state, action: PayloadAction<ModalData>) => {
      state.modal = {
        open: true,
        data: action.payload,
      };
    },
    closeModal: (state) => {
      state.modal = {
        open: false,
        data: null,
      };
    },

    setPreselectedRecipesModal: (
      state,
      action: PayloadAction<IPreselectedRecipesModal>,
    ) => {
      state.preselectedRecipes = action.payload;
    },
    resetPreselectedRecipesModal: (state) => ({
      ...state,
      preselectedRecipes: initialState.preselectedRecipes,
    }),

    toggleCartModal: (
      state,
      action: PayloadAction<Omit<ICartModal, "open">>,
    ) => {
      state.cart.open = !state.cart.open;

      state.cart.week = action.payload.week;
      state.cart.year = action.payload.year;
    },

    setCartModalOpen: (
      state,
      action: PayloadAction<Omit<ICartModal, "open">>,
    ) => {
      state.cart.open = true;

      state.cart.week = action.payload.week;
      state.cart.year = action.payload.year;
    },

    setCartModalClosed: (state) => {
      state.cart.open = false;
    },

    toggleDrawerModal: (state) => {
      state.drawer.open = !state.drawer.open;
    },

    showDrawerModal: (state) => {
      state.drawer.open = true;
    },

    hideDrawerModal: (state) => {
      state.drawer.open = false;
    },

    showLoginModal: (state) => {
      state.login.open = true;
    },

    hideLoginModal: (state) => {
      state.login.open = false;
    },

    showRecipeActionModal: (state) => {
      state.recipeAction.open = true;
    },

    hideFavoriteModal: (state) => {
      state.recipeAction.open = false;
    },
  },
});

export const {
  openModal,
  closeModal,
  toggleCartModal,
  setCartModalClosed,
  setCartModalOpen,
  toggleDrawerModal,
  showDrawerModal,
  hideDrawerModal,
  showLoginModal,
  hideLoginModal,
  showRecipeActionModal,
  hideFavoriteModal,
  setPreselectedRecipesModal,
  resetPreselectedRecipesModal,
} = modalSlice.actions;

export const selectCartModalIsOpen = (state: RootState) =>
  state.modal.cart.open;

export const selectCartModal = (state: RootState) => state.modal.cart;

export const selectDrawerModalIsOpen = (state: RootState) =>
  state.modal.drawer.open;

export const selectLoginModalIsOpen = (state: RootState) =>
  state.modal.login.open;

export const selectRecipeActionModalIsOpen = (state: RootState) =>
  state.modal.recipeAction.open;

export const selectPreselectedRecipesModal = (state: RootState) =>
  state.modal.preselectedRecipes;

export default modalSlice.reducer;
